import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import DatePicker from "react-datepicker";
import Select from "react-select";
import { format, parseISO } from "date-fns";
import LoadingDots from "../../../shared/LoadingDots";
import {
  fetchDispensationLogs,
  addLog,
} from "../../../../ReduxSlices/Prescriptions/dispensationLogSlice";
import { baseSelectStyles } from "../../../shared/selectStyles";

const CustomInput = React.forwardRef(({ value, onClick, placeholder }, ref) => (
  <input
    ref={ref}
    value={value ? format(new Date(value), "do MMM, yyyy") : ""}
    onClick={onClick}
    readOnly
    placeholder={placeholder}
    className="date-picker-input"
    style={{
      marginLeft: "10px",
      backgroundColor: "#E9F7EE",
      border: "1px solid #A5D6A7",
      borderRadius: "8px",
      padding: "0 4px",
      height: "30px",
      fontSize: "13px",
      cursor: "pointer",
    }}
  />
));

const filterOptions = [
  { value: "today", label: "Today" },
  { value: "month", label: "This Month" },
  { value: "year", label: "This Year" },
  { value: "custom", label: "Specific Day" },
  { value: "range", label: "Date Range" },
];

const DrugsSoldReport = () => {
  const dispatch = useDispatch();
  const { logs, loading, error } = useSelector((state) => state.dispensationLogs);

  const [filter, setFilter] = useState("month");
  const [customDate, setCustomDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Fetch once on mount
  useEffect(() => {
    if (logs.length === 0) {
      dispatch(fetchDispensationLogs());
    }

  }, [dispatch, logs.length]);

  // Filter logs based on selected filter
  const filteredLogs = useMemo(() => {
    const now = new Date();
    return logs.filter((log) => {
      if (!log.dispensed_at) return false;
      const logDate = parseISO(log.dispensed_at);

      switch (filter) {
        case "today":
          return logDate.toDateString() === now.toDateString();
        case "month":
          return (
            logDate.getMonth() === now.getMonth() &&
            logDate.getFullYear() === now.getFullYear()
          );
        case "year":
          return logDate.getFullYear() === now.getFullYear();
        case "custom":
          return customDate
            ? logDate.toDateString() === new Date(customDate).toDateString()
            : true;
        case "range":
          if (!startDate || !endDate) return false;
          const start = new Date(startDate);
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          return logDate >= start && logDate <= end;
        default:
          return true;
      }
    });
  }, [logs, filter, customDate, startDate, endDate]);

  // Group by drug name for chart
  const chartData = useMemo(() => {
    const counts = {};
    filteredLogs.forEach((log) => {
      log.medications.forEach((med) => {
        const name = med.name || "Unknown";
        counts[name] = (counts[name] || 0) + med.quantity;
      });
    });

    return Object.keys(counts)
      .map((name) => ({ name, quantity: counts[name] }))
      .sort((a, b) => b.quantity - a.quantity);
  }, [filteredLogs]);

  // Download PDF
  const downloadPDF = async () => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("filter", filter);
      if (filter === "custom" && customDate)
        queryParams.append("customDate", customDate);
      if (filter === "range" && startDate && endDate) {
        queryParams.append("startDate", startDate);
        queryParams.append("endDate", endDate);
      }

      const res = await fetch(
        `/api/v1/admin/reports/drugs_dispensed_pdf?${queryParams.toString()}`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      if (!res.ok) throw new Error("Failed to download PDF");

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `drugs_dispensed_${filter}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("PDF download failed:", err);
    }
  };

  return (
    <div className="p-4 bg-white border rounded shadow-md">
      <h2 className="text-2xl font-bold mb-4 text-center">
        Drugs Dispensed Report
      </h2>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6 justify-center">
        <Select
          value={filterOptions.find((opt) => opt.value === filter)}
          onChange={(opt) => {
            setFilter(opt.value);
            if (opt.value !== "custom") setCustomDate("");
            if (opt.value !== "range") {
              setStartDate("");
              setEndDate("");
            }
          }}
          options={filterOptions}
          placeholder="Select filter"
         styles={baseSelectStyles}
         
        />

        {filter === "custom" && (
          <DatePicker
            selected={customDate ? new Date(customDate) : null}
            onChange={(date) =>
              setCustomDate(date ? date.toISOString().slice(0, 10) : "")
            }
            customInput={<CustomInput placeholder="Select a date" />}
            placeholderText="Select a date"
          />
        )}

        {filter === "range" && (
          <>
            <DatePicker
              selected={startDate ? new Date(startDate) : null}
              onChange={(date) =>
                setStartDate(date ? date.toISOString().slice(0, 10) : "")
              }
              selectsStart
              startDate={startDate ? new Date(startDate) : null}
              endDate={endDate ? new Date(endDate) : null}
              customInput={<CustomInput placeholder="Start date" />}
              placeholderText="Start date"
            />
            <div className="ml-[30px]">
              <DatePicker
                selected={endDate ? new Date(endDate) : null}
                onChange={(date) =>
                  setEndDate(date ? date.toISOString().slice(0, 10) : "")
                }
                selectsEnd
                startDate={startDate ? new Date(startDate) : null}
                endDate={endDate ? new Date(endDate) : null}
                minDate={startDate ? new Date(startDate) : null}
                customInput={<CustomInput placeholder="End date" />}
                placeholderText="End date"
              />
            </div>
          </>
        )}

        <button onClick={downloadPDF} className="flex ml-[3rem] primary wide">
          Download PDF
        </button>
      </div>

      {/* Chart Section */}
      {loading ? (
        <LoadingDots />
      ) : error ? (
        <p className="text-center text-red-500">Error: {error}</p>
      ) : chartData.length === 0 ? (
        <p className="text-center text-gray-500">
          No drugs dispensed for selected period.
        </p>
      ) : (
        <ResponsiveContainer width="100%" height={400}>
          <BarChart
            layout="vertical"
            data={chartData}
            margin={{ top: 20, right: 30, left: 100, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" allowDecimals={false} />
            <YAxis type="category" dataKey="name" fontFamily="Rubik" fontSize={15} color="#333333" />
            <Tooltip formatter={(value) => [`${value} units`, "Quantity"]} />
            <Bar dataKey="quantity" fill="#4CAF50" barSize={20} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default DrugsSoldReport;
